/**
 * The patient account (Product 2026-09-06) — verified against cms-backend
 * src/payments/{dto,payments.controller,payments.service,engine}.ts at main
 * 2c166d1; contract doc docs/api/patient-payments.md.
 *
 * Every EFFECTIVE delivered session is a charge; money received is a
 * payment, optionally EARMARKED to one package. The backend applies money
 * to charges itself (oldest charge first; earmarked money only to its own
 * package's sessions; general money to anything) and keeps every
 * application as an append-only allocation. Debt, credit and prepayment are
 * DERIVED; nothing is edited or deleted — a mistaken payment is REVERSED
 * with a reason and recorded again. The client renders what comes back and
 * does no balance arithmetic of its own.
 */

import { apiRequest } from '../api/http';
import {
  buildQueryParam,
  type PagedResult,
  type QueryFilter,
  type QuerySort,
} from '../api/querySpec';
import type { PaymentMethod } from './sessionsApi';

export interface OpenCharge {
  /** ZOY-775: 'session' = a delivered session; 'extra' = an extra charge (absent on older reads = session). */
  kind?: 'session' | 'extra';
  /** The session — for an extra charge, the session it is linked to (null when unlinked). */
  sessionId: string | null;
  /** The extra charge's id; null / absent for a session. */
  chargeId?: string | null;
  /** The session's delivery time, or the extra charge's charge time. */
  deliveredAt: string;
  packageId: string | null;
  /** The extra charge's kind name as of creation. */
  kindName?: string | null;
  /** The extra charge is off its kind's default (or free-priced): a mark, no approval. */
  flagged?: boolean;
  payableAmount: string;
  paidAmount: string;
  outstandingAmount: string;
}

/** An open extra charge (never a session). */
export const isExtraCharge = (c: OpenCharge): boolean => c.kind === 'extra' && !!c.chargeId;

/** The patient's money header — the ONLY source for debt / credit / net. */
export interface PatientAccount {
  /** Debt: Σ (payableAmount − paidAmount) over effective sessions. */
  outstandingAmount: string;
  /** Credit / prepayment: Σ unapplied recorded payments (general + earmarked). */
  creditAmount: string;
  creditGeneral: string;
  creditByPackage: { packageId: string; packageName: string; amount: string }[];
  /** credit − outstanding; negative = the patient owes. */
  netBalance: string;
  openCharges: OpenCharge[];
}

export const ALLOCATION_REASONS = [
  'auto',
  'session_direct',
  'charge_direct',
  'session_voided',
  'charge_voided',
  'payment_reversed',
  'amount_reduced',
] as const;
export type AllocationReason = (typeof ALLOCATION_REASONS)[number];

export interface PaymentAllocation {
  /** ZOY-775: what the row settles (absent on older reads = session). */
  kind?: 'session' | 'extra';
  /** Null for an extra charge's row. */
  sessionId: string | null;
  chargeId?: string | null;
  /** Negative = a release (void, reversal, amount reduced). */
  amount: string;
  reason: AllocationReason | (string & {});
  createdAt: string;
  /** ZOY-647: the session's service and delivery date ride the allocation (absent on older reads); ZOY-775: the extra's kind name and charge time. */
  serviceName?: string;
  deliveredAt?: string;
}

export type PaymentRowStatus = 'recorded' | 'reversed';

export interface PatientPayment {
  id: string;
  patientId: string;
  amount: string;
  method: PaymentMethod;
  receivedAt: string;
  note: string | null;
  status: PaymentRowStatus;
  packageId: string | null;
  packageName: string | null;
  appliedAmount: string;
  /** ZOY-719: the part of this payment returned to the patient. */
  refundedAmount?: string;
  /** Zero for a reversed row; net of what was applied and refunded. */
  unappliedAmount: string;
  reversedAt: string | null;
  reversedBy: string | null;
  reversalReason: string | null;
  recordedBy: string;
  createdAt: string;
  allocations: PaymentAllocation[];
}

export interface RecordPaymentBody {
  amount: number;
  method: PaymentMethod;
  /** ISO instant; omitted = now. */
  receivedAt?: string;
  /** Earmark: this money may only settle THIS package's sessions. */
  packageId?: string;
  note?: string;
}

export const PaymentErrorCode = {
  /** ZOY-719: more than the open credit was asked for. */
  RefundExceedsCredit: 'REFUND_EXCEEDS_CREDIT',
  /** ZOY-719: a refunded payment can no longer be reversed. */
  Refunded: 'PAYMENT_REFUNDED',
  NotFound: 'PAYMENT_NOT_FOUND',
  AlreadyReversed: 'PAYMENT_ALREADY_REVERSED',
  PackageMismatch: 'PAYMENT_PACKAGE_MISMATCH',
  DateInvalid: 'PAYMENT_DATE_INVALID',
  /** The session-directed receipt: no such delivered session in this clinic. */
  SessionNotFound: 'SESSION_NOT_FOUND',
  /** Money cannot be received for a voided delivery — reinstate it first, or record on the account. */
  SessionVoided: 'PAYMENT_SESSION_VOIDED',
  /** ZOY-775: a named extra charge is not this patient's (or is gone). */
  ChargeNotFound: 'CHARGE_NOT_FOUND',
  /** ZOY-775: money cannot be received for a voided extra charge. */
  ChargeVoided: 'PAYMENT_CHARGE_VOIDED',
} as const;

/**
 * Money received FOR one delivered session (Product 2026-09-20, cms-backend
 * 9b557bf): the same receipt as the account route minus the earmark, which
 * the session decides (its own package; general for an ad-hoc session).
 */
export interface RecordSessionReceiptBody {
  amount: number;
  method: PaymentMethod;
  /** ISO instant; omitted = now. For money that actually arrived earlier. */
  receivedAt?: string;
  note?: string;
  /** ZOY-775: this patient's effective extra charges to settle right after the session, in order. */
  chargeIds?: string[];
}

/** What the directed receipt leaves behind: the payment, the session's fresh payment facts, the account. */
export interface SessionPaymentReceipt {
  payment: PatientPayment;
  session: {
    id: string;
    patientId: string;
    payableAmount: string;
    paidAmount: string;
    paymentStatus: 'unpaid' | 'partial' | 'paid';
    paymentMethod: PaymentMethod | null;
    paidAt: string | null;
  };
  account: PatientAccount;
}

/* ------------------------------ reads (sessions.read) ------------------------------ */

export const fetchPatientAccount = (patientId: string) =>
  apiRequest<PatientAccount>({ base: 'clinic', path: `/v1/patients/${patientId}/account` });

export const listPatientPayments = (patientId: string) =>
  apiRequest<{ items: PatientPayment[]; refunds?: PatientRefund[] }>({
    base: 'clinic',
    path: `/v1/patients/${patientId}/payments`,
  });

/** ZOY-719: money RETURNED to the patient out of unapplied credit. */
export interface PatientRefund {
  id: string;
  patientId: string;
  packageId: string | null;
  amount: string;
  method: PaymentMethod;
  refundedAt: string;
  note: string | null;
  recordedBy: string;
  createdAt: string;
}

export interface RecordRefundBody {
  amount: number;
  method: PaymentMethod;
  /** Refund only this package's earmarked credit; omitted = general first, then earmarked. */
  packageId?: string;
  note?: string;
}

/** 409 REFUND_EXCEEDS_CREDIT carries `availableAmount` in the error body. */
export const recordPatientRefund = (patientId: string, body: RecordRefundBody) =>
  apiRequest<{ refund: PatientRefund; account: PatientAccount }>({
    base: 'clinic',
    method: 'POST',
    path: `/v1/patients/${patientId}/refunds`,
    body,
  });

/* ------------------------- writes (sessions.payment.record) ------------------------ */

/** Money received; applied to open charges immediately. The response `account` is the fresh header. */
export const recordPatientPayment = (patientId: string, body: RecordPaymentBody) =>
  apiRequest<{ payment: PatientPayment; account: PatientAccount }>({
    base: 'clinic',
    method: 'POST',
    path: `/v1/patients/${patientId}/payments`,
    body,
  });

/**
 * Any positive amount, applied to THIS session first (`session_direct`, up to
 * what it still owes); the remainder follows the ordinary settlement — the
 * patient's other open charges oldest first (inside the session's package
 * when it has one), else credit. 409 PAYMENT_SESSION_VOIDED for a voided row.
 */
export const recordSessionReceipt = (sessionId: string, body: RecordSessionReceiptBody) =>
  apiRequest<SessionPaymentReceipt>({
    base: 'clinic',
    method: 'POST',
    path: `/v1/delivered-sessions/${sessionId}/payments`,
    body,
  });

/** No edit, no delete: a wrong payment is reversed (with a reason) and recorded again. */
export const reversePayment = (paymentId: string, reason: string) =>
  apiRequest<{ payment: PatientPayment; account: PatientAccount }>({
    base: 'clinic',
    method: 'POST',
    path: `/v1/payments/${paymentId}/reverse`,
    body: { reason },
  });

/* ------------------------ clinic-wide payment history (ZOY-647) ------------------------ */

/**
 * GET /v1/payments?query= (clinic.sessions.read): ONE ROW PER PAYMENT record —
 * account-level and session-recorded alike, reversed rows inline with their
 * status. The period axis is the payment's `receivedAt`, the same as the
 * Received report, so the two agree for a window. Sessions marked paid before
 * the payments ledger existed have no payment record and never appear.
 */
export interface PaymentHistoryRow extends PatientPayment {
  patientFirstName: string;
  patientLastName: string;
  patientRecordNumber: number | null;
}

/** Over the whole filtered set — never the page. Reversed is reported apart, never netted. */
export interface PaymentHistorySummary {
  receivedCount: number;
  receivedAmount: string;
  reversedCount: number;
  reversedAmount: string;
}

export interface PaymentHistoryPage extends PagedResult<PaymentHistoryRow> {
  summary: PaymentHistorySummary;
}

export interface PaymentHistoryFilters {
  patientId?: string;
  /** Received on or after / before (clinic-local day bounds resolved by the caller). */
  from?: Date;
  to?: Date;
  method?: PaymentMethod;
  status?: PaymentRowStatus;
  packageId?: string;
}

/** The allow-listed filters in the verified DSL shape (receivedAt, method, status, patientId, packageId). */
export function paymentFilters(f: PaymentHistoryFilters): QueryFilter[] {
  const out: QueryFilter[] = [];
  if (f.patientId) out.push({ field: 'patientId', op: 'eq', value: f.patientId });
  if (f.packageId) out.push({ field: 'packageId', op: 'eq', value: f.packageId });
  if (f.from && f.to)
    out.push({
      field: 'receivedAt',
      op: 'between',
      value: [f.from.toISOString(), f.to.toISOString()],
    });
  else if (f.from) out.push({ field: 'receivedAt', op: 'gte', value: f.from.toISOString() });
  else if (f.to) out.push({ field: 'receivedAt', op: 'lte', value: f.to.toISOString() });
  if (f.method) out.push({ field: 'method', op: 'eq', value: f.method });
  if (f.status) out.push({ field: 'status', op: 'eq', value: f.status });
  return out;
}

export const listPayments = (spec: {
  filters: QueryFilter[];
  sort: QuerySort[];
  limit: number;
  page?: number;
  cursor?: string;
}) =>
  apiRequest<PaymentHistoryPage>({
    base: 'clinic',
    path: '/v1/payments',
    query: { query: buildQueryParam(spec) },
  });

/* ---------------------------- clinic reports (sessions.read) ----------------------- */

export interface PatientBalanceRow {
  patientId: string;
  firstName: string;
  lastName: string;
  recordNumber: number | null;
  outstandingAmount: string;
  creditAmount: string;
  netBalance: string;
}

export interface PatientBalancesReport {
  items: PatientBalanceRow[];
  /** More patients than `limit` — the totals still cover only the listed rows. */
  truncated: boolean;
  totals: {
    patientsWithDebt: number;
    patientsWithCredit: number;
    outstandingAmount: string;
    creditAmount: string;
  };
}

/** Every patient with debt or credit, largest debt first (limit 1..1000, default 200). */
export const fetchPatientBalances = (limit = 200) =>
  apiRequest<PatientBalancesReport>({
    base: 'clinic',
    path: '/v1/reports/patient-balances',
    query: { limit: String(limit) },
  });

export interface PaymentsReceivedReport {
  from: string | null;
  to: string | null;
  receivedCount: number;
  /** Recorded (not reversed) payments in the window. */
  receivedAmount: string;
  /** Reversed payments in the window, reported separately. */
  reversedAmount: string;
  byMethod: Partial<Record<PaymentMethod, { count: number; amount: string }>>;
}

export const fetchPaymentsReceived = (from: Date, to: Date) =>
  apiRequest<PaymentsReceivedReport>({
    base: 'clinic',
    path: '/v1/reports/payments-received',
    query: { from: from.toISOString(), to: to.toISOString() },
  });

/* ----------------------------- extra charges (ZOY-775) ----------------------------- */

/**
 * Extra charges (Owner 2026-10-09; cms-backend 898ab76, docs/api/patient-
 * payments.md "Extra charges"): a clinic-managed KIND list and charge rows
 * beside sessions — its own debt, never an edit of the session's price. The
 * backend settles them as general money only (a package earmark never
 * reaches an extra); void is a stamp with a reason. The client proposes the
 * kind's default amount for convenience and lets the backend judge the rest.
 */
export const EXTRA_CHARGE_PRICINGS = ['time', 'fixed', 'free'] as const;
export type ExtraChargePricing = (typeof EXTRA_CHARGE_PRICINGS)[number];

export interface ExtraChargeKind {
  id: string;
  name: string;
  pricing: ExtraChargePricing;
  /** time: the rate per 15 minutes; fixed: the default amount; free: null. Storage currency. */
  unitAmount: string | null;
  /** Time kinds only: the minutes count in the practitioner's working time. */
  countsAsPractitionerTime: boolean;
  active: boolean;
  sortOrder: number;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface PatientCharge {
  id: string;
  patientId: string;
  kindId: string;
  kindName: string;
  pricing: ExtraChargePricing;
  sessionId: string | null;
  practitionerUid: string | null;
  minutes: number | null;
  amount: string;
  note: string | null;
  flagged: boolean;
  chargedAt: string;
  /** false once voided. */
  effective: boolean;
  voidedAt: string | null;
  voidedBy: string | null;
  voidReason: string | null;
  paidAmount: string;
  paymentStatus: 'unpaid' | 'partial' | 'paid';
  paymentMethod: PaymentMethod | null;
  paidAt: string | null;
  recordedBy: string;
  createdAt: string;
}

export const ChargeErrorCode = {
  KindNotFound: 'EXTRA_CHARGE_KIND_NOT_FOUND',
  KindDisabled: 'EXTRA_CHARGE_KIND_DISABLED',
  KindUnitAmountInvalid: 'EXTRA_CHARGE_KIND_UNIT_AMOUNT_INVALID',
  NotFound: 'CHARGE_NOT_FOUND',
  AlreadyVoided: 'CHARGE_ALREADY_VOIDED',
  SessionNotFound: 'SESSION_NOT_FOUND',
  SessionVoided: 'CHARGE_SESSION_VOIDED',
  MinutesRequired: 'CHARGE_MINUTES_REQUIRED',
  AmountRequired: 'CHARGE_AMOUNT_REQUIRED',
  NoteRequired: 'CHARGE_NOTE_REQUIRED',
  PractitionerRequired: 'CHARGE_PRACTITIONER_REQUIRED',
  DateInvalid: 'CHARGE_DATE_INVALID',
} as const;

/** The add-extra picker: ACTIVE kinds, picker order (clinic.sessions.read). */
export const listActiveExtraChargeKinds = () =>
  apiRequest<{ items: ExtraChargeKind[] }>({ base: 'clinic', path: '/v1/extra-charge-kinds' });

/** Clinic setup: every kind, disabled ones included (clinic.settings.read). */
export const listExtraChargeKinds = () =>
  apiRequest<{ items: ExtraChargeKind[] }>({
    base: 'clinic',
    path: '/v1/settings/extra-charge-kinds',
  });

export interface CreateExtraChargeKindBody {
  name: string;
  pricing: ExtraChargePricing;
  /** Required for time / fixed, absent for free. Storage currency. */
  unitAmount?: number;
  countsAsPractitionerTime?: boolean;
  sortOrder?: number;
}
export const createExtraChargeKind = (body: CreateExtraChargeKindBody) =>
  apiRequest<ExtraChargeKind>({
    base: 'clinic',
    method: 'POST',
    path: '/v1/settings/extra-charge-kinds',
    body,
  });

export interface UpdateExtraChargeKindBody {
  name?: string;
  unitAmount?: number;
  countsAsPractitionerTime?: boolean;
  sortOrder?: number;
}
export const patchExtraChargeKind = (id: string, body: UpdateExtraChargeKindBody) =>
  apiRequest<ExtraChargeKind>({
    base: 'clinic',
    method: 'PATCH',
    path: `/v1/settings/extra-charge-kinds/${encodeURIComponent(id)}`,
    body,
  });

export const setExtraChargeKindActive = (id: string, active: boolean) =>
  apiRequest<ExtraChargeKind>({
    base: 'clinic',
    method: 'POST',
    path: `/v1/settings/extra-charge-kinds/${encodeURIComponent(id)}/${active ? 'enable' : 'disable'}`,
  });

export interface CreatePatientChargeBody {
  kindId: string;
  /** Storage currency. Omit to take the kind's default; required for a free-priced kind. */
  amount?: number;
  /** Time kinds: 5–480. */
  minutes?: number;
  practitionerUid?: string;
  sessionId?: string;
  /** Required when the charge is flagged (free-priced or off-default). */
  note?: string;
  chargedAt?: string;
}
export const createPatientCharge = (patientId: string, body: CreatePatientChargeBody) =>
  apiRequest<{ charge: PatientCharge; account: PatientAccount }>({
    base: 'clinic',
    method: 'POST',
    path: `/v1/patients/${encodeURIComponent(patientId)}/charges`,
    body,
  });

export const listPatientCharges = (patientId: string, sessionId?: string) =>
  apiRequest<{ items: PatientCharge[] }>({
    base: 'clinic',
    path: `/v1/patients/${encodeURIComponent(patientId)}/charges`,
    ...(sessionId ? { query: { sessionId } } : {}),
  });

export const voidPatientCharge = (chargeId: string, reason: string) =>
  apiRequest<{ charge: PatientCharge; account: PatientAccount }>({
    base: 'clinic',
    method: 'POST',
    path: `/v1/charges/${encodeURIComponent(chargeId)}/void`,
    body: { reason },
  });

/** Money received FOR one extra charge: applied to it first, the remainder settles as usual. */
export const recordChargeReceipt = (
  patientId: string,
  chargeId: string,
  body: Omit<RecordSessionReceiptBody, 'chargeIds'>,
) =>
  apiRequest<{
    payment: PatientPayment;
    charge: Pick<
      PatientCharge,
      'id' | 'patientId' | 'amount' | 'paidAmount' | 'paymentStatus' | 'paymentMethod' | 'paidAt'
    >;
    account: PatientAccount;
  }>({
    base: 'clinic',
    method: 'POST',
    path: `/v1/patients/${encodeURIComponent(patientId)}/charges/${encodeURIComponent(chargeId)}/payments`,
    body,
  });
